import React, { useState, useEffect, useRef } from "react";
import { X, Check, Plus, Trash2 } from "lucide-react";

const PRESET_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#06b6d4",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
];

const AssignmentPopup = ({
  isOpen,
  onClose,
  song,
  projects,
  tags,
  onUpdateOrganization,
  onCreateProject,
  onCreateTag,
  onDeleteProject,
  onDeleteTag,
  canCreateMoreProjects,
  canCreateMoreTags,
  isMobile,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTag, setShowNewTag] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectColor, setNewProjectColor] = useState(PRESET_COLORS[0]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(PRESET_COLORS[4]);
  const overlayRef = useRef(null);

  // Sync local state with song props when song changes
  useEffect(() => {
    if (song) {
      setSelectedProjectId(song.projectId || null);
      const songTagIds =
        song.tagIds || (song.tags ? song.tags.map((t) => t.id) : []);
      setSelectedTagIds(songTagIds);
    }
  }, [song]);

  // Handle click outside
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !song) return null;

  const handleProjectSelect = async (projectId) => {
    const newProjectId = projectId === selectedProjectId ? null : projectId;
    setSelectedProjectId(newProjectId);
    try {
      await onUpdateOrganization(song.id, {
        projectId: newProjectId,
        tagIds: selectedTagIds,
      });
    } catch (error) {
      // Revert on failure
      setSelectedProjectId(selectedProjectId);
      console.error("Failed to update project:", error);
    }
  };

  const handleTagToggle = async (tagId) => {
    const newTagIds = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];
    setSelectedTagIds(newTagIds);
    try {
      await onUpdateOrganization(song.id, {
        projectId: selectedProjectId,
        tagIds: newTagIds,
      });
    } catch (error) {
      // Revert on failure
      setSelectedTagIds(selectedTagIds);
      console.error("Failed to update tags:", error);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const newProject = await onCreateProject(
        newProjectName.trim(),
        newProjectColor,
      );
      setShowNewProject(false);
      setNewProjectName("");
      // Auto-select the new project
      if (newProject) {
        const pid = newProject.id;
        setSelectedProjectId(pid);
        onUpdateOrganization(song.id, {
          projectId: pid,
          tagIds: selectedTagIds,
        });
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const newTag = await onCreateTag(newTagName.trim(), newTagColor);
      setShowNewTag(false);
      setNewTagName("");
      // Auto-select the new tag
      if (newTag) {
        const tid = newTag.id;
        const newTagIds = [...selectedTagIds, tid];
        setSelectedTagIds(newTagIds);
        onUpdateOrganization(song.id, {
          projectId: selectedProjectId,
          tagIds: newTagIds,
        });
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    const project = projects.find((p) => p.id === projectId);
    if (
      !confirm(`Delete project "${project?.name}"? Songs will be unassigned.`)
    )
      return;
    try {
      await onDeleteProject(projectId);
      if (selectedProjectId === projectId) setSelectedProjectId(null);
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const handleDeleteTag = async (e, tagId) => {
    e.stopPropagation();
    const tag = tags.find((t) => t.id === tagId);
    if (
      !confirm(`Delete tag "${tag?.name}"? It will be removed from all songs.`)
    )
      return;
    try {
      await onDeleteTag(tagId);
      setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
    } catch (error) {
      console.error("Failed to delete tag:", error);
    }
  };

  const content = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        flex: 1,
      }}
    >
      {/* Header — fixed at top, never scrolls */}
      <div className="flex items-center justify-between p-4 border-b border-forest-600 flex-shrink-0 bg-forest-800">
        <h3 className="text-sm font-medium text-forest-100 truncate pr-2">
          Organize &ldquo;{song.title}&rdquo;
        </h3>
        <button
          onClick={onClose}
          className="text-forest-400 hover:text-forest-200 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-y-auto p-4 space-y-4" style={{ flex: 1 }}>
        {/* Project Section */}
        <div>
          <p className="text-forest-300 text-sm mb-2">Project</p>
          <div className="space-y-1">
            <button
              onClick={() => handleProjectSelect(null)}
              className={`w-full text-left text-sm px-3 py-2 rounded transition-colors ${
                selectedProjectId === null
                  ? "bg-forest-700 text-gold-500"
                  : "text-forest-200 hover:bg-forest-700"
              }`}
            >
              None
            </button>
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => handleProjectSelect(project.id)}
                className={`w-full text-left text-sm px-3 py-2 rounded transition-colors flex items-center gap-2 ${
                  selectedProjectId === project.id
                    ? "bg-forest-700 text-gold-500"
                    : "text-forest-200 hover:bg-forest-700"
                }`}
              >
                {project.color && (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: project.color }}
                  />
                )}
                <span className="flex-1 truncate">{project.name}</span>
                {onDeleteProject && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleDeleteProject(e, project.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleDeleteProject(e, project.id);
                    }}
                    className="bg-ember-500 hover:bg-ember-400 transition-colors"
                    title="Delete project"
                    style={{
                      marginLeft: "auto",
                      padding: "4px",
                      borderRadius: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Trash2
                      style={{
                        width: "12px",
                        height: "12px",
                        color: "#1a1a2e",
                      }}
                    />
                  </span>
                )}
              </button>
            ))}

            {/* New project inline form */}
            {showNewProject ? (
              <div className="p-2 space-y-2 border border-forest-600 rounded">
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
                  placeholder="Project name"
                  className="w-full text-sm px-2 py-1.5 rounded bg-forest-900 border border-forest-600 text-forest-100 placeholder-forest-400"
                  autoFocus
                />
                <div className="flex items-center gap-1">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNewProjectColor(c)}
                      className="rounded-full"
                      style={{
                        backgroundColor: c,
                        width: newProjectColor === c ? "28px" : "22px",
                        height: newProjectColor === c ? "28px" : "22px",
                        border:
                          newProjectColor === c
                            ? "3px solid white"
                            : "2px solid transparent",
                        transition: "all 0.15s ease",
                      }}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCreateProject}
                    className="text-xs px-3 py-1 rounded bg-gold-500 text-forest-900"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => setShowNewProject(false)}
                    className="text-xs px-3 py-1 rounded text-forest-300 hover:text-forest-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              canCreateMoreProjects && (
                <button
                  onClick={() => setShowNewProject(true)}
                  className="w-full text-left text-sm px-3 py-2 rounded text-gold-500 hover:bg-forest-700 inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  New project...
                </button>
              )
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-forest-600" />

        {/* Tags Section */}
        <div>
          <p className="text-forest-300 text-sm mb-2">Tags</p>
          <div className="space-y-1">
            {tags.map((tag) => {
              const isActive = selectedTagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  onClick={() => handleTagToggle(tag.id)}
                  className={`w-full text-left text-sm px-3 py-2 rounded transition-colors flex items-center gap-2 ${
                    isActive
                      ? "bg-forest-700 text-gold-500"
                      : "text-forest-200 hover:bg-forest-700"
                  }`}
                >
                  {isActive && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                  {tag.color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                  )}
                  <span className="flex-1 truncate">{tag.name}</span>
                  {onDeleteTag && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleDeleteTag(e, tag.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleDeleteTag(e, tag.id);
                      }}
                      className="bg-ember-500 hover:bg-ember-400 transition-colors"
                      title="Delete tag"
                      style={{
                        marginLeft: "auto",
                        padding: "4px",
                        borderRadius: "4px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Trash2
                        style={{
                          width: "12px",
                          height: "12px",
                          color: "#1a1a2e",
                        }}
                      />
                    </span>
                  )}
                </button>
              );
            })}

            {/* New tag inline form */}
            {showNewTag ? (
              <div className="w-full p-2 space-y-2 border border-forest-600 rounded mt-1">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateTag()}
                  placeholder="Tag name"
                  className="w-full text-sm px-2 py-1.5 rounded bg-forest-900 border border-forest-600 text-forest-100 placeholder-forest-400"
                  autoFocus
                />
                <div className="flex items-center gap-1">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNewTagColor(c)}
                      className="rounded-full"
                      style={{
                        backgroundColor: c,
                        width: newTagColor === c ? "28px" : "22px",
                        height: newTagColor === c ? "28px" : "22px",
                        border:
                          newTagColor === c
                            ? "3px solid white"
                            : "2px solid transparent",
                        transition: "all 0.15s ease",
                      }}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCreateTag}
                    className="text-xs px-3 py-1 rounded bg-gold-500 text-forest-900"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => setShowNewTag(false)}
                    className="text-xs px-3 py-1 rounded text-forest-300 hover:text-forest-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              canCreateMoreTags && (
                <button
                  onClick={() => setShowNewTag(true)}
                  className="w-full text-left text-sm px-3 py-2 rounded text-gold-500 hover:bg-forest-700 inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  New tag...
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    // Full-height sheet above nav bar
    return (
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end"
        style={{ paddingBottom: "64px" }}
        onClick={(e) => {
          if (e.target === overlayRef.current) onClose();
        }}
      >
        <div
          className="w-full bg-forest-800 rounded-t-xl shadow-xl flex flex-col"
          style={{
            height: "100%",
            paddingTop: "env(safe-area-inset-top, 24px)",
          }}
        >
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 bg-forest-600 rounded-full" />
          </div>
          {content}
        </div>
      </div>
    );
  }

  // Desktop popup
  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        className="bg-forest-800 shadow-xl"
        style={{
          width: "360px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          border: "2px solid var(--color-gold-500, #d4a843)",
          overflow: "hidden",
        }}
      >
        {content}
      </div>
    </div>
  );
};

export default AssignmentPopup;
